import { useCallback, useState } from 'react';

import type { Guide } from '../../guidance/types/Guide';
import { useUser } from '../../user/providers/UserProvider';
import type { GuidanceSessionType } from '../types/GuidanceSessions';
import { useBookGuidanceSession } from './useBookGuidanceSession';
import { useSwitchMembershipGuide } from './useSwitchMembershipGuide';

interface BookAvailableGuideParams {
    selectedGuide: Guide;
    membershipGuideId: string | undefined;
    sessionType: GuidanceSessionType;
    startTimeUtcDate: Date;
}

export const useBookAvailableGuide = () => {
    const { switchMembershipGuide } = useSwitchMembershipGuide();
    const { bookGuidanceSession } = useBookGuidanceSession();
    const { setOptimisticUserData } = useUser();

    const [isBooking, setIsBooking] = useState(false);

    const bookAvailableGuide = useCallback(
        async (params: BookAvailableGuideParams) => {
            const {
                selectedGuide,
                membershipGuideId,
                sessionType,
                startTimeUtcDate,
            } = params;
            const isGuideSwitch = selectedGuide.id !== membershipGuideId;

            setIsBooking(true);
            try {
                if (isGuideSwitch) {
                    await switchMembershipGuide({
                        originalGuideId: membershipGuideId ?? '',
                        newGuideId: selectedGuide.id,
                        shouldRefetchUser: false, // refetching user too soon can cause higher level re-renders that we don't want to happen before we book the session
                    });
                }

                const startTimeUtc = startTimeUtcDate.toISOString();
                const userTimeZone =
                    Intl.DateTimeFormat().resolvedOptions().timeZone;

                await bookGuidanceSession({
                    guideId: selectedGuide.id,
                    sessionType,
                    startTimeUtc,
                    userTimeZone,
                });

                if (isGuideSwitch) {
                    setOptimisticUserData?.((prevUser) => ({
                        ...prevUser,
                        membership:
                            prevUser.membership == null
                                ? prevUser.membership
                                : {
                                      ...prevUser.membership,
                                      guideId: selectedGuide.id,
                                      guide: selectedGuide,
                                  },
                    }));
                }
            } finally {
                setIsBooking(false);
            }
        },
        [bookGuidanceSession, switchMembershipGuide, setOptimisticUserData],
    );

    return { bookAvailableGuide, isBooking };
};
